import {
  useAppSelector,
  useAppDispatch,
  selectWeekStartsOn,
  setWeekStartsOn,
  type WeekStart,
  selectShoppingGroupBy,
  setShoppingGroupBy,
  type ShoppingGroupBy,
  selectRecipeLayout,
  setRecipeLayout,
  type RecipeLayout,
  selectUnitSystem,
  setUnitSystem,
  type UnitSystem,
  selectShowDuration,
  selectShowDifficulty,
  selectShowServings,
  selectShowRating,
  selectShowTags,
  selectAutoShowTimer,
  selectTimerSound,
  selectTimerFlash,
  setRecipeMetaVisible,
  setAutoShowTimer,
  setTimerSound,
  setTimerFlash,
  selectLocaleMode,
  setLocaleMode,
  selectHomeCards,
  selectHomeCardOrder,
  setHomeCardVisible,
  setHomeCardOrder,
  resetHomeLayout,
  type HomeCard,
  useT,
  type LocaleMode,
} from "@reciperoll/core";
import { useTheme, type ThemePref } from "../lib/theme";
import { SortableList, GripHandle } from "../components/SortableList";

// A larger heading that groups related settings below it. Settings were added in
// the order they came up; the grouping (General → Recipes → Planning & shopping)
// clusters them by scope, most-global first. Mirrors the RN Preferences screen.
function GroupHeader({ children, first }: { children: string; first?: boolean }) {
  return (
    <h2
      className={`text-lg font-bold text-ink border-b border-line pb-2 mb-2 ${
        first ? "mt-2" : "mt-9"
      }`}
    >
      {children}
    </h2>
  );
}

// A per-setting sub-label. `first` tightens the gap under a group header.
function SectionLabel({ children, first }: { children: string; first?: boolean }) {
  return (
    <p
      className={`text-sm font-bold text-ink-soft uppercase tracking-wider mb-2 ${
        first ? "mt-3" : "mt-7"
      }`}
    >
      {children}
    </p>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return <div className="bg-surface-2 rounded-2xl overflow-hidden">{children}</div>;
}

// A labelled on/off switch row.
function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <span className="text-ink font-medium">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
        className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer shrink-0 ${
          checked ? "bg-terracotta" : "bg-line"
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
            checked ? "translate-x-5" : ""
          }`}
        />
      </button>
    </div>
  );
}

// Device-local display preferences, split out from the Account page so neither
// gets crowded. Mirrors the RN Preferences screen.
export default function Preferences() {
  const t = useT();
  const dispatch = useAppDispatch();
  const [themePref, setThemePref] = useTheme();
  const localeMode = useAppSelector(selectLocaleMode);
  const weekStartsOn = useAppSelector(selectWeekStartsOn);
  const shoppingGroupBy = useAppSelector(selectShoppingGroupBy);
  const recipeLayout = useAppSelector(selectRecipeLayout);
  const unitSystem = useAppSelector(selectUnitSystem);
  const showDuration = useAppSelector(selectShowDuration);
  const showDifficulty = useAppSelector(selectShowDifficulty);
  const showServings = useAppSelector(selectShowServings);
  const showRating = useAppSelector(selectShowRating);
  const showTags = useAppSelector(selectShowTags);
  const autoShowTimer = useAppSelector(selectAutoShowTimer);
  const timerSound = useAppSelector(selectTimerSound);
  const timerFlash = useAppSelector(selectTimerFlash);
  const homeCards = useAppSelector(selectHomeCards);
  const homeOrder = useAppSelector(selectHomeCardOrder);

  const resetHome = () => {
    if (window.confirm(t("customiseHome.resetMessage"))) dispatch(resetHomeLayout());
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="font-display text-3xl font-extrabold text-ink mb-6">{t("prefs.title")}</h1>

      {/* ── General ── */}
      <GroupHeader first>{t("prefs.groupGeneral")}</GroupHeader>

      <SectionLabel first>{t("prefs.appearance")}</SectionLabel>
      <Card>
        <div className="p-4">
          <div className="flex gap-2">
            {([
              ["light", t("prefs.light")],
              ["dark", t("prefs.dark")],
              ["system", t("prefs.system")],
            ] as [ThemePref, string][]).map(([opt, label]) => (
              <button
                key={opt}
                type="button"
                onClick={() => setThemePref(opt)}
                className={`flex-1 rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors cursor-pointer ${
                  themePref === opt
                    ? "bg-terracotta border-terracotta text-white"
                    : "bg-surface border-line text-ink-soft hover:bg-surface-2"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </Card>

      <SectionLabel>{t("prefs.language")}</SectionLabel>
      <Card>
        <div className="p-4">
          <div className="relative">
            <select
              value={localeMode}
              onChange={(e) => dispatch(setLocaleMode(e.target.value as LocaleMode))}
              className="w-full appearance-none rounded-xl border border-line bg-surface px-3.5 py-2.5 pr-10 text-sm font-semibold text-ink cursor-pointer hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-terracotta/40"
            >
              {([
                ["system", t("prefs.system")],
                ["en", t("prefs.langEnglishUK")],
                ["en-US", t("prefs.langEnglishUS")],
                ["en-CA", t("prefs.langEnglishCA")],
                ["es", t("prefs.langSpanish")],
              ] as [LocaleMode, string][]).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-ink-faint">
              ▾
            </span>
          </div>
          <p className="text-sm text-ink-faint mt-3">
            {t("prefs.languageHint")}
          </p>
        </div>
      </Card>

      {/* ── Home screen ── */}
      <GroupHeader>{t("prefs.homeScreen")}</GroupHeader>
      <p className="text-sm text-ink-faint mb-3">{t("customiseHome.hint")}</p>
      <SortableList
        items={homeOrder.map((c) => ({ id: c }))}
        onReorder={(items) =>
          dispatch(setHomeCardOrder(items.map((i) => i.id as HomeCard)))
        }
        className="space-y-2"
        renderItem={(item, handle) => {
          const card = item.id as HomeCard;
          const visible = homeCards[card];
          const label = t(`customiseHome.cards.${card}`);
          return (
            <div className="flex items-center gap-2 bg-surface-2 rounded-2xl px-3 py-2.5">
              <GripHandle handle={handle} className="w-6 h-8" />
              <div className="flex-1">
                <span className="text-ink font-medium">{label}</span>
                {card === "search" && (
                  <p className="text-xs text-ink-faint mt-0.5">
                    {t("customiseHome.searchWebNote")}
                  </p>
                )}
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={visible}
                aria-label={label}
                onClick={() =>
                  dispatch(setHomeCardVisible({ card, visible: !visible }))
                }
                className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer shrink-0 ${
                  visible ? "bg-terracotta" : "bg-line"
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                    visible ? "translate-x-5" : ""
                  }`}
                />
              </button>
            </div>
          );
        }}
      />
      <button
        type="button"
        onClick={resetHome}
        className="mt-3 text-sm font-semibold text-terracotta hover:underline cursor-pointer"
      >
        {t("customiseHome.reset")}
      </button>

      {/* ── Recipes ── */}
      <GroupHeader>{t("prefs.groupRecipes")}</GroupHeader>

      <SectionLabel first>{t("prefs.recipeLayout")}</SectionLabel>
      <Card>
        <div className="p-4">
          <div className="flex gap-2">
            {([
              ["tabs", t("prefs.tabs")],
              ["single", t("prefs.singlePage")],
            ] as [RecipeLayout, string][]).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => dispatch(setRecipeLayout(value))}
                className={`flex-1 rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors cursor-pointer ${
                  recipeLayout === value
                    ? "bg-terracotta border-terracotta text-white"
                    : "bg-surface border-line text-ink-soft hover:bg-surface-2"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="text-sm text-ink-faint mt-3">
            {t("prefs.recipeLayoutHint")}
          </p>
        </div>
      </Card>

      <SectionLabel>{t("prefs.units")}</SectionLabel>
      <Card>
        <div className="p-4">
          <div className="flex gap-2">
            {([
              ["original", t("prefs.asWritten")],
              ["metric", t("prefs.metric")],
              ["imperial", t("prefs.imperial")],
            ] as [UnitSystem, string][]).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => dispatch(setUnitSystem(value))}
                className={`flex-1 rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors cursor-pointer ${
                  unitSystem === value
                    ? "bg-terracotta border-terracotta text-white"
                    : "bg-surface border-line text-ink-soft hover:bg-surface-2"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="text-sm text-ink-faint mt-3">
            {t("prefs.unitsHint")}
          </p>
        </div>
      </Card>

      <SectionLabel>{t("prefs.recipeDetails")}</SectionLabel>
      <Card>
        <div className="divide-y divide-line">
          <ToggleRow
            label={t("form.duration")}
            checked={showDuration}
            onChange={(v) =>
              dispatch(setRecipeMetaVisible({ field: "showDuration", value: v }))
            }
          />
          <ToggleRow
            label={t("recipe.difficulty")}
            checked={showDifficulty}
            onChange={(v) =>
              dispatch(setRecipeMetaVisible({ field: "showDifficulty", value: v }))
            }
          />
          <ToggleRow
            label={t("recipe.servings")}
            checked={showServings}
            onChange={(v) =>
              dispatch(setRecipeMetaVisible({ field: "showServings", value: v }))
            }
          />
          <ToggleRow
            label={t("recipe.rating")}
            checked={showRating}
            onChange={(v) =>
              dispatch(setRecipeMetaVisible({ field: "showRating", value: v }))
            }
          />
          <ToggleRow
            label={t("form.tags")}
            checked={showTags}
            onChange={(v) =>
              dispatch(setRecipeMetaVisible({ field: "showTags", value: v }))
            }
          />
        </div>
        <p className="text-sm text-ink-faint px-4 py-3 border-t border-line">
          {t("prefs.recipeDetailsHint")}
        </p>
      </Card>

      <SectionLabel>{t("prefs.cookMode")}</SectionLabel>
      <Card>
        <div className="divide-y divide-line">
          <ToggleRow
            label={t("prefs.autoShowTimer")}
            checked={autoShowTimer}
            onChange={(v) => dispatch(setAutoShowTimer(v))}
          />
          <ToggleRow
            label={t("prefs.timerSound")}
            checked={timerSound}
            onChange={(v) => dispatch(setTimerSound(v))}
          />
          <ToggleRow
            label={t("prefs.timerFlash")}
            checked={timerFlash}
            onChange={(v) => dispatch(setTimerFlash(v))}
          />
        </div>
        <p className="text-sm text-ink-faint px-4 py-3 border-t border-line">
          {t("prefs.cookTimerHint")}
        </p>
      </Card>

      {/* ── Planning & shopping ── */}
      <GroupHeader>{t("prefs.groupPlanning")}</GroupHeader>

      <SectionLabel first>{t("prefs.startOfWeek")}</SectionLabel>
      <Card>
        <div className="p-4">
          <div className="flex gap-2">
            {([
              [1, t("prefs.mon")],
              [2, t("prefs.tue")],
              [3, t("prefs.wed")],
              [4, t("prefs.thu")],
              [5, t("prefs.fri")],
              [6, t("prefs.sat")],
              [0, t("prefs.sun")],
            ] as [WeekStart, string][]).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => dispatch(setWeekStartsOn(value))}
                className={`flex-1 rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors cursor-pointer ${
                  weekStartsOn === value
                    ? "bg-terracotta border-terracotta text-white"
                    : "bg-surface border-line text-ink-soft hover:bg-surface-2"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="text-sm text-ink-faint mt-3">
            {t("prefs.startOfWeekHint")}
          </p>
        </div>
      </Card>

      <SectionLabel>{t("prefs.shoppingGrouping")}</SectionLabel>
      <Card>
        <div className="p-4">
          <div className="flex gap-2">
            {([
              ["aisle", t("shopping.byAisle")],
              ["recipe", t("shopping.byRecipe")],
            ] as [ShoppingGroupBy, string][]).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => dispatch(setShoppingGroupBy(value))}
                className={`flex-1 rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors cursor-pointer ${
                  shoppingGroupBy === value
                    ? "bg-terracotta border-terracotta text-white"
                    : "bg-surface border-line text-ink-soft hover:bg-surface-2"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="text-sm text-ink-faint mt-3">
            {t("prefs.shoppingGroupingHint")}
          </p>
        </div>
      </Card>
    </div>
  );
}
